import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadVoice } from '@/lib/queries';
import { cn } from '@/lib/cn';

/** What went wrong, in words the person can act on (2026-10-25: every failure used to read
 *  "refused", which hid a site header blocking the microphone for everyone). */
function micError(e: unknown): string {
  const name = (e as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    // a site policy blocks it before the browser ever asks; otherwise the person said no
    const policy = (document as { permissionsPolicy?: { allowsFeature(f: string): boolean } }).permissionsPolicy;
    if (policy && !policy.allowsFeature('microphone')) return 'The site does not allow the microphone yet. Tell a manager.';
    return 'The microphone is blocked for this site. Click the lock icon in the address bar, allow the microphone, and try again.';
  }
  if (name === 'NotFoundError') return 'No microphone was found on this device';
  if (name === 'NotReadableError') return 'The microphone is in use by another app. Close it and try again.';
  return 'Could not start recording';
}

/**
 * One-button voice note. Press to record, press again to stop; the clip is
 * uploaded straight to storage and the storage path handed to the parent,
 * which attaches it to a comment or review. Preview and delete before posting.
 */
export default function VoiceRecorder({ value, onChange, disabled, compact }: {
  value: { path: string; url: string } | null;
  onChange: (v: { path: string; url: string } | null) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => { if (timerRef.current) window.clearInterval(timerRef.current); recRef.current?.stream.getTracks().forEach((t) => t.stop()); }, []);

  async function start() {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('This browser cannot record audio'); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((m) => MediaRecorder.isTypeSupported(m)) ?? '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        if (blob.size < 1000) { toast.error('Nothing was recorded'); return; }
        setUploading(true);
        try {
          const path = await uploadVoice(blob);
          onChange({ path, url: URL.createObjectURL(blob) });
        } catch { toast.error('Could not upload the voice note'); }
        finally { setUploading(false); }
      };
      rec.start(250);
      recRef.current = rec;
      setRecording(true); setSeconds(0);
      timerRef.current = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) { toast.error(micError(e)); }
  }

  function stop() {
    recRef.current?.stop(); recRef.current = null;
    setRecording(false);
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
  }

  if (value) {
    return (
      <div className={cn('flex items-center gap-2', compact ? 'text-[11px]' : 'text-xs')}>
        <audio controls src={value.url} className="h-8 max-w-[220px]" />
        <button type="button" onClick={() => onChange(null)} disabled={disabled} className="text-muted hover:text-red-300 inline-flex items-center gap-1" title="Discard voice note">
          <Trash2 size={12} /> {compact ? '' : 'discard'}
        </button>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || uploading}
      title={recording ? 'Stop recording' : 'Record a voice note'}
      className={cn('inline-flex items-center gap-1.5 rounded-lg border px-2.5 h-8 text-[11px] transition',
        recording ? 'border-red-500/60 bg-red-600/15 text-red-300' : 'border-slate-800 text-muted hover:text-muted-strong hover:border-slate-700',
        (disabled || uploading) && 'opacity-50 cursor-not-allowed')}
    >
      {recording ? <Square size={12} fill="currentColor" /> : <Mic size={12} />}
      {uploading ? 'Uploading...' : recording ? `Stop ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : compact ? '' : 'Voice note'}
    </button>
  );
}
